import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { AlertTriangle } from "lucide-react";

const IDLE_LIMIT_MS = 30 * 60 * 1000; // 30 min
const WARN_MS = 2 * 60 * 1000;         // warn 2 min before

export default function IdleTimer() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const lastActivity = useRef(Date.now());
  const [showWarn, setShowWarn] = useState(false);
  const [remaining, setRemaining] = useState(WARN_MS);

  useEffect(() => {
    if (!user) return;
    const bump = () => {
      lastActivity.current = Date.now();
      if (showWarn) setShowWarn(false);
    };
    const events = ["mousedown", "keydown", "scroll", "touchstart"];
    events.forEach(e => window.addEventListener(e, bump));
    const iv = setInterval(async () => {
      const idle = Date.now() - lastActivity.current;
      if (idle >= IDLE_LIMIT_MS) {
        clearInterval(iv);
        await logout();
        navigate("/login");
      } else if (idle >= IDLE_LIMIT_MS - WARN_MS) {
        setShowWarn(true);
        setRemaining(IDLE_LIMIT_MS - idle);
      }
    }, 5000);
    return () => {
      clearInterval(iv);
      events.forEach(e => window.removeEventListener(e, bump));
    };
    // eslint-disable-next-line
  }, [user]);

  const stay = () => {
    lastActivity.current = Date.now();
    setShowWarn(false);
  };

  if (!user || !showWarn) return null;
  const mins = Math.max(0, Math.floor(remaining / 60000));
  const secs = Math.max(0, Math.floor((remaining % 60000) / 1000));

  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/60 flex items-center justify-center p-4" data-testid="idle-warning-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <div className="font-heading text-lg font-semibold text-slate-900">You will be signed out soon</div>
            <p className="text-sm text-slate-500 mt-1">
              You've been inactive for a while. Your session will end in about{" "}
              <span className="font-semibold text-slate-900" data-testid="idle-remaining">
                {mins}m {secs}s
              </span>.
            </p>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={async () => { await logout(); navigate("/login"); }}
            className="px-4 py-2 rounded-md text-sm border border-slate-300 hover:bg-slate-50"
            data-testid="idle-logout-now">
            Sign out now
          </button>
          <button onClick={stay} data-testid="idle-stay-signed-in"
            className="px-4 py-2 rounded-md text-sm bg-brand-blue bg-brand-blue-hover text-white">
            Stay signed in
          </button>
        </div>
      </div>
    </div>
  );
}
